/* SetupWizard — first-time onboarding overlay.
 *
 * Auto-opens for brand-new workspaces (no bank, no contacts, no folders, not
 * yet dismissed). 3-step flow:
 *
 *   1. Connect a bank      → Plaid Link (or "Skip — I'll add manually")
 *   2. Add first contact   → inline name/email (or "Skip")
 *   3. Apply doc templates → uses existing /setup-suggestions endpoint
 *
 * On completion (last step's primary CTA OR explicit Skip-all):
 *   POST /api/workspaces/{id}/setup-apply (if templates picked)
 *   POST /api/workspaces/{id}/setup-skip  (if user skips all)
 *
 * Visual language inherits BrandEmptyState / LandingPageDark — gradient mesh,
 * mono uppercase overlines, emerald primary CTAs.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { useWorkspace } from '../context/WorkspaceContext';
import { apiFetch } from '../fetchInterceptor';
import { track } from '../lib/telemetry';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Sparkles, ArrowRight, ArrowLeft, Check, Loader2,
  Building2, Users, FolderPlus, X, ShieldCheck,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { BankLimitUpsellModal } from './banking/BankLimitUpsellModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STEPS = ['bank', 'contacts', 'documents'];


function StepDot({ index, currentIndex, complete }) {
  const isCurrent = index === currentIndex;
  const isPast = index < currentIndex || complete;
  return (
    <div className={cn(
      'flex items-center gap-2',
      index < STEPS.length - 1 && 'flex-1',
    )}>
      <div className={cn(
        'flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold border-2 transition-colors',
        isPast
          ? 'bg-emerald-500 border-emerald-500 text-black'
          : isCurrent
            ? 'bg-emerald-500/15 border-emerald-500/60 text-emerald-600 dark:text-emerald-400'
            : 'bg-transparent border-zinc-300 dark:border-white/10 text-zinc-400 dark:text-zinc-600'
      )}>
        {isPast ? <Check className="w-3.5 h-3.5" /> : index + 1}
      </div>
      {index < STEPS.length - 1 && (
        <div className={cn(
          'flex-1 h-px transition-colors',
          isPast ? 'bg-emerald-500/60' : 'bg-zinc-200 dark:bg-white/10',
        )} />
      )}
    </div>
  );
}


function Overline({ children }) {
  return (
    <p className="text-[10px] font-mono uppercase tracking-[0.28em] text-zinc-500 dark:text-zinc-600 mb-2">
      {children}
    </p>
  );
}


export default function SetupWizard() {
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.workspace_id;

  const [open, setOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [progress, setProgress] = useState(null);
  const [completing, setCompleting] = useState(false);

  // Step 1 — Bank connect via Plaid
  const [linkToken, setLinkToken] = useState(null);
  const [bankLoading, setBankLoading] = useState(false);
  // Pre-flight: how many Plaid banks this user's plan allows
  const [plaidLimits, setPlaidLimits] = useState(null); // { used, limit, planName }
  const [bankUpsell, setBankUpsell] = useState(null);   // { addon_price, next_plan }
  const [upsellOpen, setUpsellOpen] = useState(false);

  // Step 2 — Contact inline form
  const [contact, setContact] = useState({ name: '', email: '', type: 'customer' });
  const [contactSaving, setContactSaving] = useState(false);

  // Step 3 — Templates
  const [suggestions, setSuggestions] = useState([]);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  // ── Lifecycle: poll progress on workspace change ────────────────────────
  const fetchProgress = useCallback(async () => {
    if (!wsId) return;
    try {
      const r = await apiFetch(`${API_URL}/api/workspaces/${wsId}/onboarding-progress`);
      if (r.ok) {
        const data = await r.json();
        setProgress(data);
        if (data.should_show && !open) {
          setOpen(true);
          setStepIndex(0);
          track('wizard.auto_opened', { workspace_name: data.workspace_name }, wsId);
        }
      }
    } catch (_e) { /* silent */ }
  }, [wsId, open]);

  useEffect(() => { fetchProgress(); }, [fetchProgress]);

  // Allow other components (e.g. FinishSetupBanner) to re-open the wizard
  // on demand, even after the user dismissed the initial auto-open.
  useEffect(() => {
    const handleOpen = (e) => {
      const startStep = e?.detail?.startStep;
      if (typeof startStep === 'number' && startStep >= 0 && startStep < STEPS.length) {
        setStepIndex(startStep);
      } else {
        setStepIndex(0);
      }
      setOpen(true);
      track('wizard.opened', { start_step: typeof startStep === 'number' ? startStep : 0 }, wsId);
      // Refresh progress so step UI reflects the current state
      fetchProgress();
    };
    window.addEventListener('open-setup-wizard', handleOpen);
    return () => window.removeEventListener('open-setup-wizard', handleOpen);
  }, [fetchProgress]);

  // Load template suggestions when reaching step 3
  useEffect(() => {
    if (!open || stepIndex !== 2 || suggestions.length > 0) return;
    (async () => {
      setTemplatesLoading(true);
      try {
        const r = await apiFetch(`${API_URL}/api/workspaces/${wsId}/setup-suggestions`);
        if (r.ok) {
          const data = await r.json();
          setSuggestions(data.suggestions || []);
          // Pre-select up to 2 most-relevant by default
          setSelectedTemplates((data.suggestions || []).slice(0, 2).map(s => s.template_id));
        }
      } catch (_e) { /* silent */ }
      setTemplatesLoading(false);
    })();
  }, [open, stepIndex, suggestions.length, wsId]);

  // ── Plaid Link setup ────────────────────────────────────────────────────
  // Pre-flight: fetch current plan's Plaid limit so we can block linking before
  // Plaid Link opens (no point making the user authenticate their bank only to
  // hit a 403 from the exchange step).
  const fetchPlaidLimits = useCallback(async () => {
    try {
      const r = await apiFetch(`${API_URL}/api/billing/credits`);
      if (r.ok) {
        const d = await r.json();
        const fresh = {
          used: d.plaid_accounts_used || 0,
          limit: d.plaid_accounts_limit || 0,
          planName: d.plan?.name || 'No Plan',
        };
        setPlaidLimits(fresh);
        setBankUpsell(d.bank_upsell || null);
        return fresh;
      }
    } catch (_e) { /* silent */ }
    return null;
  }, []);

  useEffect(() => { fetchPlaidLimits(); }, [fetchPlaidLimits]);

  const fetchLinkToken = async () => {
    if (!wsId) {
      toast.error('No workspace selected.');
      return;
    }
    // Pre-flight guard: if user has no Plaid quota, don't even open Plaid Link.
    if (plaidLimits && plaidLimits.used >= plaidLimits.limit) {
      // Showing the inline upgrade card inside StepBank — no toast spam.
      return;
    }
    setBankLoading(true);
    try {
      const r = await apiFetch(`${API_URL}/api/plaid/link_token?workspace_id=${encodeURIComponent(wsId)}`, { method: 'POST' });
      if (r.ok) {
        const data = await r.json();
        setLinkToken(data.link_token);
      } else {
        let detail = '';
        try { const j = await r.json(); detail = j.detail || ''; } catch { /* ignore */ }
        toast.error(detail || 'Could not start Plaid — try the Banking page later.');
        setBankLoading(false);
      }
    } catch (e) {
      toast.error('Plaid is unavailable right now.');
      setBankLoading(false);
    }
  };

  const onPlaidSuccess = useCallback(async (publicToken) => {
    try {
      const r = await apiFetch(
        `${API_URL}/api/plaid/exchange_public_token?workspace_id=${wsId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ public_token: publicToken }),
        }
      );
      if (r.ok) {
        toast.success('Bank connected ✓');
        track('wizard.step_completed', { step: 'bank' }, wsId);
        await fetchProgress();
        setStepIndex(1);
        track('wizard.step_advanced', { to_step: 'contacts' }, wsId);
      } else {
        // Surface the backend's actual reason (e.g. "Bank account limit reached…")
        let detail = '';
        try { const j = await r.json(); detail = j.detail || ''; } catch { /* ignore */ }
        if (r.status === 403 && detail.toLowerCase().includes('limit')) {
          toast.error(detail, {
            action: {
              label: 'Upgrade',
              onClick: () => { window.location.href = '/settings?tab=billing'; },
            },
            duration: 10000,
          });
        } else {
          toast.error(detail || 'Bank connection failed');
        }
      }
    } catch (e) {
      toast.error('Bank connection failed');
    } finally {
      setLinkToken(null);
      setBankLoading(false);
      // Re-open the wizard so the user lands on the next step (Contacts)
      setOpen(true);
    }
  }, [wsId, fetchProgress]);

  const { open: openPlaid, ready: plaidReady } = usePlaidLink({
    token: linkToken,
    onSuccess: onPlaidSuccess,
    onExit: () => {
      setLinkToken(null);
      setBankLoading(false);
      // Re-open the wizard after Plaid Link closes (user dismissed)
      setOpen(true);
    },
  });

  useEffect(() => {
    if (linkToken && plaidReady) {
      // Hide the wizard's Radix Dialog before opening Plaid so its focus-trap +
      // aria-hidden doesn't block clicks on the Plaid Link iframe.
      setOpen(false);
      // Small delay lets the Dialog's portal unmount before Plaid mounts.
      const t = setTimeout(() => openPlaid(), 150);
      return () => clearTimeout(t);
    }
  }, [linkToken, plaidReady, openPlaid]);

  // ── Contact save (step 2) ───────────────────────────────────────────────
  const saveContact = async () => {
    if (!contact.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setContactSaving(true);
    try {
      const r = await apiFetch(`${API_URL}/api/workspaces/${wsId}/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: contact.name.trim(),
          email: contact.email.trim() || null,
          contact_type: contact.type,
          entity_type: 'organization',
        }),
      });
      if (r.ok) {
        toast.success(`${contact.type === 'customer' ? 'Customer' : 'Vendor'} added ✓`);
        track('wizard.step_completed', { step: 'contacts', contact_type: contact.type }, wsId);
        await fetchProgress();
        setStepIndex(2);
        track('wizard.step_advanced', { to_step: 'documents' }, wsId);
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Could not add contact');
      }
    } catch (e) {
      toast.error('Could not add contact');
    }
    setContactSaving(false);
  };

  // ── Templates apply / Skip ──────────────────────────────────────────────
  const completeWizard = async (applyTemplates = true) => {
    setCompleting(true);
    try {
      if (applyTemplates && selectedTemplates.length > 0) {
        const r = await apiFetch(`${API_URL}/api/workspaces/${wsId}/setup-apply`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ template_ids: selectedTemplates }),
        });
        if (r.ok) {
          toast.success(`${selectedTemplates.length} template${selectedTemplates.length === 1 ? '' : 's'} applied — your folders are ready ✓`);
        }
      } else {
        await apiFetch(`${API_URL}/api/workspaces/${wsId}/setup-skip`, { method: 'POST' });
      }
      track('wizard.finished', {
        templates_applied: applyTemplates ? selectedTemplates.length : 0,
      }, wsId);
      setOpen(false);
      // Hard refresh so all dashboards reload with new data
      setTimeout(() => window.location.reload(), 400);
    } catch (e) {
      toast.error('Could not finish setup');
    }
    setCompleting(false);
  };

  const dismissForever = async () => {
    await apiFetch(`${API_URL}/api/workspaces/${wsId}/setup-skip`, { method: 'POST' });
    track('wizard.dismissed', { at_step: STEPS[stepIndex] }, wsId);
    setOpen(false);
  };

  // ── Render ──────────────────────────────────────────────────────────────
  if (!progress) return null;
  const currentStep = STEPS[stepIndex];

  return (
    <>
    <Dialog open={open} onOpenChange={(v) => !v && dismissForever()}>
      <DialogContent
        className="max-w-2xl p-0 overflow-hidden bg-white dark:bg-black border border-zinc-200 dark:border-white/10"
        data-testid="setup-wizard"
        data-tour-block
        hideCloseButton
      >
        <DialogTitle className="sr-only">Set up your CountBucks workspace</DialogTitle>
        <DialogDescription className="sr-only">
          Three quick steps to get your workspace fully populated.
        </DialogDescription>

        {/* Mesh background — subtle in light, dramatic in dark */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden opacity-60 dark:opacity-100">
          <div className="absolute top-[-25%] left-[-10%] w-[400px] h-[400px] rounded-full bg-emerald-500/15 dark:bg-emerald-500/10 blur-[100px]" />
          <div className="absolute bottom-[-30%] right-[-10%] w-[400px] h-[400px] rounded-full bg-violet-500/15 dark:bg-violet-500/10 blur-[100px]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(0,0,0,0.04)_1px,transparent_0)] dark:bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.03)_1px,transparent_0)] [background-size:32px_32px]" />
        </div>

        <div className="relative z-10">
          {/* Header */}
          <div className="px-8 pt-7 pb-5 border-b border-zinc-200 dark:border-white/10">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-violet-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                  <Sparkles className="w-5 h-5 text-black" />
                </div>
                <div>
                  <Overline>Welcome · 2 minute setup</Overline>
                  <h2 className="text-xl font-bold tracking-tight text-zinc-900 dark:text-white">
                    Get {progress.workspace_name || 'your workspace'}{' '}
                    <span className="bg-gradient-to-r from-emerald-500 to-violet-500 bg-clip-text text-transparent">production-ready.</span>
                  </h2>
                </div>
              </div>
              <button
                type="button"
                onClick={dismissForever}
                aria-label="Skip setup"
                className="p-1 rounded-md text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-black/5 dark:hover:bg-white/5"
                data-testid="setup-wizard-dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Step indicator */}
            <div className="flex items-center gap-3 pt-2">
              {STEPS.map((s, i) => (
                <StepDot
                  key={s}
                  index={i}
                  currentIndex={stepIndex}
                  complete={progress.steps[s]?.done}
                />
              ))}
            </div>
          </div>

          {/* Body — one step at a time */}
          <div className="px-8 py-8 min-h-[280px]" data-testid={`setup-step-${currentStep}`}>
            {currentStep === 'bank' && (
              <StepBank
                done={progress.steps.bank.done}
                loading={bankLoading}
                onConnect={fetchLinkToken}
                onSkip={() => setStepIndex(1)}
                plaidLimits={plaidLimits}
                bankUpsell={bankUpsell}
                onUpsell={() => setUpsellOpen(true)}
              />
            )}
            {currentStep === 'contacts' && (
              <StepContacts
                done={progress.steps.contacts.done}
                contact={contact}
                setContact={setContact}
                saving={contactSaving}
                onSave={saveContact}
                onSkip={() => setStepIndex(2)}
              />
            )}
            {currentStep === 'documents' && (
              <StepDocuments
                loading={templatesLoading}
                suggestions={suggestions}
                selected={selectedTemplates}
                toggle={(id) => {
                  setSelectedTemplates(prev =>
                    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
                  );
                }}
              />
            )}
          </div>

          {/* Footer */}
          <div className="px-8 py-4 border-t border-zinc-200 dark:border-white/10 flex items-center justify-between gap-3 bg-zinc-50/60 dark:bg-white/[0.02]">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setStepIndex(i => Math.max(0, i - 1))}
              disabled={stepIndex === 0}
              data-testid="setup-wizard-back"
              className="text-zinc-500 dark:text-zinc-400"
            >
              <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back
            </Button>
            <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-zinc-400 dark:text-zinc-600">
              <ShieldCheck className="w-3 h-3" />
              Step {stepIndex + 1} of {STEPS.length}
            </div>
            {currentStep === 'documents' ? (
              <Button
                type="button"
                onClick={() => completeWizard(true)}
                disabled={completing}
                className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold gap-1.5"
                data-testid="setup-wizard-finish"
              >
                {completing
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <>Finish <Check className="w-4 h-4" /></>}
              </Button>
            ) : (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setStepIndex(i => Math.min(STEPS.length - 1, i + 1))}
                className="text-zinc-500 dark:text-zinc-400"
                data-testid="setup-wizard-next"
              >
                Skip <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* In-flow bank-limit upsell — 2 clicks to authorize, then Plaid Link
        opens automatically against the freshly-raised limit. */}
    <BankLimitUpsellModal
      open={upsellOpen}
      onOpenChange={setUpsellOpen}
      limits={plaidLimits}
      upsell={bankUpsell}
      onUnlocked={async () => {
        await fetchPlaidLimits();
        fetchLinkToken(true);
      }}
    />
    </>
  );
}


// ─── Individual steps ─────────────────────────────────────────────────────

function StepBank({ done, loading, onConnect, onSkip, plaidLimits, bankUpsell, onUpsell }) {
  // At-limit detection: user has a plan but maxed out, OR plan has 0 quota.
  const atLimit = plaidLimits && plaidLimits.limit > 0 && plaidLimits.used >= plaidLimits.limit;
  const noQuota = plaidLimits && plaidLimits.limit === 0;
  const blocked = !done && (atLimit || noQuota);

  return (
    <div className="text-center max-w-md mx-auto">
      <div className="inline-flex w-14 h-14 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 items-center justify-center mb-4">
        {done
          ? <Check className="w-7 h-7 text-emerald-500" />
          : <Building2 className="w-7 h-7 text-emerald-500" />}
      </div>
      <Overline>Step 1 · Banking</Overline>
      <h3 className="text-2xl font-bold tracking-tighter text-zinc-900 dark:text-white mb-2 leading-tight">
        {done ? 'Bank connected ✓' : <>Connect your <span className="bg-gradient-to-r from-emerald-500 to-violet-500 bg-clip-text text-transparent">bank.</span></>}
      </h3>
      <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed mb-6">
        {done
          ? 'Transactions will sync automatically every day. You can continue.'
          : 'Sync from 11,000+ banks via Plaid. Auto-categorize transactions with AI. Skip this and add a bank later from the Banking page.'}
      </p>

      {/* Pre-flight upgrade panel — shown when the user's plan has no Plaid quota */}
      {blocked && (
        <div className="mb-5 p-4 rounded-xl border border-amber-500/30 bg-amber-50 dark:bg-amber-950/20 text-left" data-testid="plaid-upgrade-prompt">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                {noQuota ? 'Upgrade to connect a bank' : 'You\'ve hit your bank limit'}
              </p>
              <p className="text-xs text-amber-800/80 dark:text-amber-300/80 mt-1">
                {noQuota
                  ? `Your current plan (${plaidLimits.planName}) doesn't include Plaid bank connections. Upgrade to start auto-syncing transactions.`
                  : `Your plan allows ${plaidLimits.limit} Plaid ${plaidLimits.limit === 1 ? 'connection' : 'connections'}. Upgrade for more.`}
              </p>
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <Button
                  type="button"
                  size="sm"
                  onClick={onUpsell}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs h-8 px-3"
                  data-testid="plaid-upgrade-btn"
                >
                  {noQuota
                    ? 'Unlock bank sync'
                    : `Add +1 bank · $${Number(bankUpsell?.addon_price ?? 5).toFixed(0)}/mo`}
                  <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </Button>
                <button
                  type="button"
                  onClick={() => { window.location.href = '/settings?tab=billing'; }}
                  className="text-xs text-amber-700 dark:text-amber-300 hover:underline"
                  data-testid="plaid-see-plans"
                >
                  See plans
                </button>
                <button
                  type="button"
                  onClick={onSkip}
                  className="text-xs text-amber-700 dark:text-amber-300 hover:underline"
                  data-testid="plaid-upgrade-skip"
                >
                  Skip for now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {!done && !blocked && (
        <Button
          type="button"
          onClick={() => onConnect()}
          disabled={loading || plaidLimits === null}
          className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold h-10 px-6 gap-2"
          data-testid="setup-bank-connect"
        >
          {loading
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <>Connect with Plaid <ArrowRight className="w-4 h-4" /></>}
        </Button>
      )}
      {done && (
        <Button type="button" onClick={onSkip} className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold gap-2" data-testid="setup-bank-next">
          Continue <ArrowRight className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
}


function StepContacts({ done, contact, setContact, saving, onSave, onSkip }) {
  return (
    <div className="max-w-md mx-auto">
      <div className="text-center mb-6">
        <div className="inline-flex w-14 h-14 rounded-2xl bg-blue-500/15 border border-blue-500/30 items-center justify-center mb-4">
          {done
            ? <Check className="w-7 h-7 text-blue-500" />
            : <Users className="w-7 h-7 text-blue-500" />}
        </div>
        <Overline>Step 2 · CRM</Overline>
        <h3 className="text-2xl font-bold tracking-tighter text-zinc-900 dark:text-white mb-2 leading-tight">
          {done ? 'Contact added ✓' : <>Add your first <span className="bg-gradient-to-r from-blue-500 to-emerald-500 bg-clip-text text-transparent">contact.</span></>}
        </h3>
        <p className="text-sm text-zinc-600 dark:text-zinc-400">
          {done
            ? 'You can add more from the Contacts page anytime.'
            : 'Track who you do business with — invoice them, receive payments, watch balances.'}
        </p>
      </div>
      {!done && (
        <div className="space-y-3">
          <div className="flex gap-2">
            <Button
              type="button"
              variant={contact.type === 'customer' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setContact({ ...contact, type: 'customer' })}
              className={contact.type === 'customer' ? 'bg-emerald-500 hover:bg-emerald-400 text-black' : ''}
              data-testid="setup-contact-type-customer"
            >
              Customer
            </Button>
            <Button
              type="button"
              variant={contact.type === 'vendor' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setContact({ ...contact, type: 'vendor' })}
              className={contact.type === 'vendor' ? 'bg-emerald-500 hover:bg-emerald-400 text-black' : ''}
              data-testid="setup-contact-type-vendor"
            >
              Vendor
            </Button>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-500">Name</Label>
            <Input
              autoFocus
              value={contact.name}
              onChange={(e) => setContact({ ...contact, name: e.target.value })}
              placeholder={contact.type === 'customer' ? 'Acme Corp.' : 'AWS, Inc.'}
              data-testid="setup-contact-name"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-500">Email (optional)</Label>
            <Input
              type="email"
              value={contact.email}
              onChange={(e) => setContact({ ...contact, email: e.target.value })}
              placeholder="ap@acme.com"
              data-testid="setup-contact-email"
            />
          </div>
          <Button
            type="button"
            onClick={onSave}
            disabled={saving || !contact.name.trim()}
            className="w-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold gap-2 mt-2"
            data-testid="setup-contact-save"
          >
            {saving
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <>Add {contact.type === 'customer' ? 'Customer' : 'Vendor'} <ArrowRight className="w-4 h-4" /></>}
          </Button>
        </div>
      )}
      {done && (
        <div className="text-center">
          <Button type="button" onClick={onSkip} className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold gap-2" data-testid="setup-contact-next">
            Continue <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}


function StepDocuments({ loading, suggestions, selected, toggle }) {
  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-6">
        <div className="inline-flex w-14 h-14 rounded-2xl bg-violet-500/15 border border-violet-500/30 items-center justify-center mb-4">
          <FolderPlus className="w-7 h-7 text-violet-500" />
        </div>
        <Overline>Step 3 · Documents</Overline>
        <h3 className="text-2xl font-bold tracking-tighter text-zinc-900 dark:text-white mb-2 leading-tight">
          Pick your <span className="bg-gradient-to-r from-violet-500 to-emerald-500 bg-clip-text text-transparent">document templates.</span>
        </h3>
        <p className="text-sm text-zinc-600 dark:text-zinc-400">
          Pre-built folder structures for tax returns, payroll, contracts — pick any that apply.
        </p>
      </div>
      {loading ? (
        <div className="text-center py-8 text-zinc-500">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading templates…
        </div>
      ) : suggestions.length === 0 ? (
        <p className="text-center text-sm text-zinc-500">No templates available. You can finish and add folders later.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[260px] overflow-y-auto pr-1">
          {suggestions.map(s => {
            const isSelected = selected.includes(s.template_id);
            return (
              <button
                key={s.template_id}
                type="button"
                onClick={() => toggle(s.template_id)}
                className={cn(
                  'text-left p-3 rounded-xl border transition-all',
                  isSelected
                    ? 'border-emerald-500/60 bg-emerald-500/8 dark:bg-emerald-500/10'
                    : 'border-zinc-200 dark:border-white/10 bg-white/40 dark:bg-white/[0.02] hover:border-zinc-300 dark:hover:border-white/20',
                )}
                data-testid={`setup-template-${s.template_id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-zinc-900 dark:text-zinc-100 truncate">{s.name}</p>
                    <p className="text-[11px] text-zinc-500 dark:text-zinc-500 mt-0.5 line-clamp-2">{s.description}</p>
                    <div className="mt-2 flex gap-1.5 flex-wrap">
                      <Badge variant="outline" className="text-[9px] font-mono">{s.node_count} folders</Badge>
                      {s.industry && <Badge variant="outline" className="text-[9px] font-mono">{s.industry}</Badge>}
                    </div>
                  </div>
                  <div className={cn(
                    'flex items-center justify-center w-5 h-5 rounded border-2 flex-shrink-0',
                    isSelected
                      ? 'bg-emerald-500 border-emerald-500'
                      : 'border-zinc-300 dark:border-white/20',
                  )}>
                    {isSelected && <Check className="w-3 h-3 text-black" />}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
